// One crashing component must not take the whole app with it.
//
// Until now nothing caught render errors, so a single throw unmounted the tree and left a
// blank page — which is exactly what happened on /regions when the Google Maps bootstrap
// had defined `google.maps` but not yet `google.maps.Map`. A blank page tells a family
// member nothing and tells us nothing either.

import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  message: string | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { message: null }

  static getDerivedStateFromError(error: unknown): State {
    return { message: error instanceof Error ? error.message : String(error) }
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Kept in the console rather than sent anywhere — there is no error reporting service
    // in this project, and adding one is not this fix's job.
    console.error('SPZ crashed while rendering', error, info.componentStack)
  }

  render(): ReactNode {
    if (this.state.message === null) return this.props.children

    return (
      <main className="shell" style={{ maxWidth: '34rem', margin: '0 auto', padding: '2.5rem 1.25rem' }}>
        <h1>This screen broke</h1>
        <p>
          Something went wrong drawing this page. Nothing you typed was lost &mdash; every
          answer saves as you go.
        </p>
        <p>
          <button type="button" onClick={() => window.location.reload()} style={reloadStyle}>
            Reload the page
          </button>
        </p>
        <p>
          <a href="/">Back to the trip</a> &middot; <a href="/feedback">Tell us what happened</a>
        </p>
        <p style={{ color: 'var(--muted)', fontSize: '0.8rem', overflowWrap: 'anywhere' }}>
          {this.state.message}
        </p>
      </main>
    )
  }
}

const reloadStyle = {
  minHeight: '3rem',
  padding: '0.75rem 1.25rem',
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: '0.5rem',
  border: 'none',
  background: 'var(--accent)',
  color: '#fff',
  cursor: 'pointer',
} as const

export default ErrorBoundary
